const MOBILE_USER_AGENT =
  /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i

export const isMobileDevice = () =>
  navigator.userAgentData?.mobile === true ||
  MOBILE_USER_AGENT.test(navigator.userAgent) ||
  window.matchMedia?.('(max-width: 768px) and (pointer: coarse)').matches ===
    true

export const openPrintableDocument = (path) => {
  const separator = path.includes('?') ? '&' : '?'
  if (isMobileDevice()) {
    // iOS Safari blocks window.open outside a trusted gesture, navigate instead.
    window.location.assign(`${path}${separator}download=pdf`)
    return
  }
  window.open(path, '_blank')
}

const waitForImages = (element) =>
  Promise.all(
    Array.from(element.querySelectorAll('img')).map((image) => {
      if (image.complete) return Promise.resolve()
      return new Promise((resolve) => {
        image.addEventListener('load', resolve, { once: true })
        image.addEventListener('error', resolve, { once: true })
      })
    }),
  )

export const createElementPdf = async (element) => {
  if (!element) throw new Error('The document is not ready to download.')

  await document.fonts?.ready
  await waitForImages(element)
  const { default: html2pdf } = await import('html2pdf.js/dist/html2pdf.min.js')

  return html2pdf()
    .set({
      margin: 8,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        ignoreElements: (node) => node.dataset?.html2canvasIgnore === 'true',
      },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
    })
    .from(element)
    .toPdf()
    .outputPdf('blob')
}

// Chrome/Firefox/Edge on iOS expose window.print but throw when it is called.
const IOS_NON_SAFARI = /CriOS|FxiOS|EdgiOS|OPiOS/i

export const canPrintDocument = () =>
  typeof window.print === 'function' &&
  !IOS_NON_SAFARI.test(navigator.userAgent)

export const printWithBodyClass = (bodyClass) => {
  if (!canPrintDocument()) return
  document.body.classList.add(bodyClass)
  window.addEventListener(
    'afterprint',
    () => document.body.classList.remove(bodyClass),
    { once: true },
  )
  try {
    window.print()
  } catch {
    document.body.classList.remove(bodyClass)
  }
}

const toPdfFile = (pdfBlob, filename) =>
  new File([pdfBlob], filename, { type: 'application/pdf' })

// The Web Share API is secure-context only, so plain-http LAN testing has no share sheet.
export const canSharePdf = (pdfBlob, filename) => {
  if (!navigator.share || !navigator.canShare) return false
  try {
    return navigator.canShare({ files: [toPdfFile(pdfBlob, filename)] })
  } catch {
    return false
  }
}

export const sharePdf = (pdfBlob, filename) =>
  navigator.share({ files: [toPdfFile(pdfBlob, filename)], title: filename })

export const downloadElementAsPdf = async (element, filename) => {
  const pdfBlob = await createElementPdf(element)
  const downloadUrl = URL.createObjectURL(pdfBlob)
  const link = document.createElement('a')
  link.href = downloadUrl
  link.download = filename
  link.click()
  URL.revokeObjectURL(downloadUrl)
}
